"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  GetPartyChatSummaryResponse,
  PartyChatAccess,
  PartyChatActivity,
  PartyChatPreviewItem,
} from "../types";
import {
  hasUnreadPartyChatActivity,
  PARTY_CHAT_POLL_INTERVAL_MS,
} from "../party-chat";
import {
  getPartyChatLastReadAt,
  markPartyChatLastRead,
  type PartyChatReadStorageReader,
  type PartyChatReadStorageWriter,
} from "../party-chat-unread";

export type PartyChatUnreadStorage = PartyChatReadStorageReader &
  PartyChatReadStorageWriter;

export interface UsePartyChatUnreadOptions {
  fetchSummary: (partyId: string) => Promise<GetPartyChatSummaryResponse>;
  pollIntervalMs?: number;
  enabled?: boolean;
  initialData?: GetPartyChatSummaryResponse | null;
  storage?: PartyChatUnreadStorage | null;
}

export interface UsePartyChatUnreadResult {
  access: PartyChatAccess | null;
  latestActivity: PartyChatActivity | null;
  recent: PartyChatPreviewItem[];
  currentUserId: string | null;
  hasUnread: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markRead: (readAt?: string) => void;
}

interface PartyChatUnreadState {
  access: PartyChatAccess | null;
  latestActivity: PartyChatActivity | null;
  recent: PartyChatPreviewItem[];
  currentUserId: string | null;
  error: string | null;
  lastReadAt: string | null;
  loading: boolean;
}

function resolveStorage(
  storage: PartyChatUnreadStorage | null | undefined,
): PartyChatUnreadStorage | null {
  if (storage) {
    return storage;
  }
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage;
}

function readLastReadAt(
  storage: PartyChatUnreadStorage | null,
  partyId: string,
  userId: string | null,
): string | null {
  if (!storage || !userId) {
    return null;
  }
  return getPartyChatLastReadAt(storage, partyId, userId);
}

export function usePartyChatUnread(
  partyId: string,
  options: UsePartyChatUnreadOptions,
): UsePartyChatUnreadResult {
  const {
    fetchSummary,
    pollIntervalMs = PARTY_CHAT_POLL_INTERVAL_MS,
    enabled = true,
    initialData = null,
    storage: storageOption,
  } = options;

  const storage = resolveStorage(storageOption);
  const [state, setState] = useState<PartyChatUnreadState>(() => ({
    access: initialData?.access ?? null,
    latestActivity: initialData?.latestActivity ?? null,
    recent: initialData?.recent ?? [],
    currentUserId: initialData?.currentUserId ?? null,
    error: initialData?.error ?? null,
    lastReadAt: readLastReadAt(
      storage,
      partyId,
      initialData?.currentUserId ?? null,
    ),
    loading: !initialData,
  }));

  const fetchSummaryRef = useRef(fetchSummary);
  fetchSummaryRef.current = fetchSummary;
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!partyId || !enabled) {
      setState((previous) =>
        previous.loading ? { ...previous, loading: false } : previous,
      );
      return;
    }

    const seq = ++requestSeqRef.current;
    try {
      const data = await fetchSummaryRef.current(partyId);
      if (!mountedRef.current || seq !== requestSeqRef.current) {
        return;
      }
      setState({
        access: data.access,
        latestActivity: data.latestActivity,
        recent: data.recent ?? [],
        currentUserId: data.currentUserId,
        error: data.error,
        lastReadAt: readLastReadAt(storage, partyId, data.currentUserId),
        loading: false,
      });
    } catch (err) {
      if (!mountedRef.current || seq !== requestSeqRef.current) {
        return;
      }
      setState((previous) => ({
        ...previous,
        error: err instanceof Error ? err.message : "Failed to load chat",
        loading: false,
      }));
    }
  }, [enabled, partyId, storage]);

  useEffect(() => {
    if (!enabled || !partyId) {
      return;
    }

    void refetch();

    if (!pollIntervalMs || pollIntervalMs <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      void refetch();
    }, pollIntervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refetch();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      clearInterval(intervalId);
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
    };
  }, [enabled, partyId, pollIntervalMs, refetch]);

  const markRead = useCallback(
    (readAt?: string) => {
      if (!storage || !state.currentUserId) {
        return;
      }
      const nextReadAt =
        readAt ?? state.latestActivity?.at ?? new Date().toISOString();
      markPartyChatLastRead(storage, partyId, state.currentUserId, nextReadAt);
      setState((previous) => ({
        ...previous,
        lastReadAt: getPartyChatLastReadAt(
          storage,
          partyId,
          state.currentUserId ?? "",
        ),
      }));
    },
    [partyId, state.currentUserId, state.latestActivity, storage],
  );

  return {
    access: state.access,
    latestActivity: state.latestActivity,
    recent: state.recent,
    currentUserId: state.currentUserId,
    hasUnread: hasUnreadPartyChatActivity({
      access: state.access,
      latestActivity: state.latestActivity,
      lastReadAt: state.lastReadAt,
      currentUserId: state.currentUserId,
    }),
    loading: state.loading,
    error: state.error,
    refetch,
    markRead,
  };
}
